import { ReactElement, useMemo } from "react";
import { Box } from "@mui/material";

import { CellContext, ColumnDef } from "@tanstack/react-table";
import { Row } from "@tanstack/table-core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DataTable, VtColumnMeta } from "components/DataTable/DataTable";
import { NightReport } from "interfaces/nightReport.interface";

import { NightReportField } from "./NightReportField";

const noSleepData = (row: Row<NightReport>) =>
  row.original.state !== "PRESENCE_AND_SLEEP";

const NightReportCell = ({ row, column }: CellContext<NightReport, any>) => {
  if (noSleepData(row)) return null;
  const meta = column.columnDef.meta as VtColumnMeta;
  return <NightReportField type={meta.type} report={row.original} />;
};

type NightReportTableProps = {
  reports: NightReport[];
};

export default function NightReportTable({ reports }: NightReportTableProps) {
  const { t } = useTranslation();
  const noSleepData = (row: Row<NightReport>) =>
    row.original.state !== "PRESENCE_AND_SLEEP";

  const columns = useMemo(
    (): ColumnDef<NightReport>[] => [
      {
        accessorKey: "device.name",
        enableSorting: true,
        header: t("Device Name / Location"),
      },
      {
        accessorKey: "subject.identifier",
        enableSorting: false,
        header: t("subject"),
        meta: {
          tooltip: t("night_report_subject_info"),
        } as VtColumnMeta,
      },
      {
        accessorKey: "respiration_rate",
        enableSorting: false,
        header: t("Respiration Rate (rpm)"),
        meta: {
          tooltip: t("night_report_respiration_rate_info"),
          colSpan: (row: Row<any>) => (noSleepData(row) ? 7 : 1),
        } as VtColumnMeta,
        cell: ({
          row,
        }: CellContext<NightReport, unknown>): ReactElement<any, any> =>
          noSleepData(row) ? (
            <Box>
              {row.original.state === "NO_SLEEP" &&
                t("No sleep data recorded due to not enough sleep")}
              {row.original.state === "NO_PRESENCE" &&
                t("subject_not_present_or_data_not_available")}
            </Box>
          ) : (
            <NightReportField type={"RESPIRATION_RATE"} report={row.original} />
          ),
      },
      {
        accessorKey: "movement",
        enableSorting: false,
        header: t("Movement"),
        meta: {
          tooltip: t("night_report_movement_info"),
          skipCell: noSleepData,
          type: "MOVEMENT",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
      {
        accessorKey: "time_asleep",
        enableSorting: false,
        header: t("time_asleep"),
        meta: {
          tooltip: t("night_report_time_asleep_info"),
          skipCell: noSleepData,
          type: "TIME_ASLEEP",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
      {
        accessorKey: "sleep_score",
        enableSorting: false,
        header: t("Sleep Score"),
        meta: {
          tooltip: t("night_report_sleep_score_info"),
          skipCell: noSleepData,
          type: "SLEEP_SCORE",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
      {
        accessorKey: "out_of_bed",
        enableSorting: false,
        header: t("Out of Bed"),
        meta: {
          tooltip: t("night_report_out_of_bed_info"),
          skipCell: noSleepData,
          type: "OUT_OF_BED",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
      {
        accessorKey: "selected_session_is_in_progress",
        enableSorting: false,
        header: t("Session in Progress"),
        meta: {
          tooltip: t("night_report_selected_session_is_in_progress_info"),
          skipCell: noSleepData,
          type: "SESSION_IS_IN_PROGRESS",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
      {
        accessorKey: "baseline_night_count",
        enableSorting: false,
        header: t("Baseline Nights"),
        meta: {
          tooltip: t("night_report_baseline_night_count_info"),
          skipCell: noSleepData,
          type: "BASELINE_NIGHT_COUNT",
        } as VtColumnMeta,
        cell: NightReportCell,
      },
    ],
    [t],
  );
  const navigate = useNavigate();

  return (
    <DataTable
      tableId={"night-report"}
      getRowId={(row) => row.session_id ?? row.subject?.id ?? ""}
      columns={columns}
      data={reports}
      onRowClick={(row: NightReport) => {
        if (row.subject && row.session_id) {
          navigate(
            `/subject/${row.subject.id}/sessions?sessionId=${row.session_id}`,
          );
        }
      }}
      footerText={`${reports.length} ${reports.length === 1 ? t("Device") : t("Devices")}`}
    />
  );
}
